// src/pages/Landing/Landing.tsx

import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDemo } from '../../context/DemoContext';
import { useTour } from '../../context/TourContext';
import SignalLoader from '../../components/SignalLoader/SignalLoader';
import type { TickerBlip } from '../../components/SignalLoader/SignalLoader';
import styles from './Landing.module.css';

const FEATURES = [
  {
    icon: '\uD83D\uDCE1',
    title: 'Live Positions',
    desc: 'Auto-refreshing portfolio view with one-click close controls and real-time P&L.',
  },
  {
    icon: '\uD83E\uDD16',
    title: 'Automated Bots',
    desc: 'RSI, pullback, and options bots execute trades 24/5 with SMS alerts.',
  },
  {
    icon: '\uD83D\uDCC8',
    title: 'AI Predictions',
    desc: 'Sentiment analysis and price forecasts powered by machine learning.',
  },
  {
    icon: '\uD83D\uDEE1\uFE0F',
    title: 'Risk Management',
    desc: 'Potential outcomes modeling, strategy builder, and drawdown monitoring.',
  },
  {
    icon: '\uD83D\uDCCA',
    title: 'Market Scanner',
    desc: 'Scan for setups across tickers with configurable technical filters.',
  },
  {
    icon: '\u26A1',
    title: 'Quick Trade',
    desc: 'Enter and exit positions in seconds with pre-configured order templates.',
  },
];

const STEPS = [
  {
    num: '01',
    title: 'Connect',
    desc: 'Link your brokerage account securely. Paper or live — your choice.',
  },
  {
    num: '02',
    title: 'Configure',
    desc: 'Set your strategies, risk parameters, and alert preferences.',
  },
  {
    num: '03',
    title: 'Deploy',
    desc: 'Activate your bots and let SignalDeck execute your playbook around the clock.',
  },
];

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const { enterDemo } = useDemo();
  const { startTour } = useTour();

  // Ticker pool for the radar scanner animation — mix of normal pings and hot signals
  const heroTickers: TickerBlip[] = useMemo(
    () => [
      { symbol: 'NVDA', change: 2.3, signal: 'normal' },
      { symbol: 'SPY', change: 0.4, signal: 'normal' },
      { symbol: 'AAPL', change: -1.1, signal: 'normal' },
      { symbol: 'TSLA', change: 4.7, signal: 'hot' },
      { symbol: 'AMD', change: 1.8, signal: 'normal' },
      { symbol: 'META', change: -0.6, signal: 'normal' },
      { symbol: 'QQQ', change: 0.2, signal: 'normal' },
      { symbol: 'AMZN', change: 1.4, signal: 'normal' },
      { symbol: 'XOM', change: -2.8, signal: 'hot' },
      { symbol: 'JPM', change: 0.9, signal: 'normal' },
      { symbol: 'MSFT', change: -0.3, signal: 'normal' },
      { symbol: 'BA', change: 3.2, signal: 'normal' },
      { symbol: 'COIN', change: -5.1, signal: 'hot' },
      { symbol: 'GOOGL', change: 0.7, signal: 'normal' },
      { symbol: 'LMT', change: 1.1, signal: 'normal' },
      { symbol: 'AVGO', change: 2.6, signal: 'normal' },
      { symbol: 'GS', change: -0.9, signal: 'normal' },
      { symbol: 'PLTR', change: 6.2, signal: 'hot' },
      { symbol: 'CRM', change: 0.5, signal: 'normal' },
      { symbol: 'EQT', change: -1.7, signal: 'normal' },
    ],
    [],
  );

  const handleTakeTour = () => {
    enterDemo();
    localStorage.removeItem('tour_completed');
    startTour();
    navigate('/home');
  };

  return (
    <div className={styles.landing}>
      {/* ── Hero ── */}
      <section className={styles.hero}>
        <div className={styles.heroRadar}>
          <SignalLoader message="" size="normal" tickers={heroTickers} />
        </div>
        <h1 className={styles.heroTitle}>SignalDeck</h1>
        <p className={styles.heroTagline}>Navigate the noise. Find the signal.</p>
        <div className={styles.heroCtas}>
          <Link to="/login?mode=signUp" className={styles.ctaPrimary}>
            Get Started
          </Link>
          <Link to="/login" className={styles.ctaSecondary}>
            Sign In
          </Link>
        </div>
        <button className={styles.tourLink} onClick={handleTakeTour}>
          Take a Tour →
        </button>
      </section>

      {/* ── Features ── */}
      <section className={styles.features}>
        <h2 className={styles.sectionTitle}>Everything you need to trade smarter</h2>
        <div className={styles.featureGrid}>
          {FEATURES.map((f) => (
            <div key={f.title} className={styles.featureCard}>
              <span className={styles.featureIcon}>{f.icon}</span>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section className={styles.howItWorks}>
        <h2 className={styles.sectionTitle}>How it works</h2>
        <div className={styles.stepsRow}>
          {STEPS.map((s) => (
            <div key={s.num} className={styles.step}>
              <span className={styles.stepNum}>{s.num}</span>
              <h3 className={styles.stepTitle}>{s.title}</h3>
              <p className={styles.stepDesc}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Closing CTA ── */}
      <section className={styles.closingCta}>
        <h2 className={styles.closingTitle}>Ready to automate your edge?</h2>
        <p className={styles.closingDesc}>Start with a paper account — no risk, full functionality.</p>
        <div className={styles.heroCtas}>
          <Link to="/login?mode=signUp" className={styles.ctaPrimary}>
            Get Started
          </Link>
          <Link to="/home" className={styles.ctaSecondary}>
            Explore Demo
          </Link>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className={styles.footer}>
        <div className={styles.footerLinks}>
          <Link to="/terms" className={styles.footerLink}>
            Terms of Service
          </Link>
          <span className={styles.footerSep}>&middot;</span>
          <Link to="/privacy" className={styles.footerLink}>
            Privacy Policy
          </Link>
          <span className={styles.footerSep}>&middot;</span>
          <Link to="/risk-disclosure" className={styles.footerLink}>
            Risk Disclosure
          </Link>
        </div>
        <p>&copy; {new Date().getFullYear()} SignalDeck. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default Landing;
